'use client'
import axiosLib, { AxiosError, AxiosInstance, CreateAxiosDefaults } from 'axios'
import { uiSlice } from '../store/ui/slice'
import { store } from '../store'
import { userSlice } from '../store/user/slice'
import { router, routerObj } from '../components/routing/Router'
import i18n, { LANGUAGE_HEADER, getCurrentLanguage } from '../i18n'

export interface IRequestHandler extends CreateAxiosDefaults {
  useLoader?: boolean
  handleResponseError?: (error: AxiosError) => void
  useErrorToast?: boolean
  token?: string
}
export const RequestHandler = (props: IRequestHandler = {}): AxiosInstance => {
  const { headers = {}, useLoader = false, handleResponseError, useErrorToast = true, ...config } = props
  const user = store.getState().user
  const BASE_URL = process.env.REACT_APP_ZUMMA_SERVICE_SERVICE_URL + '/v1'
  const token = props.token || user.token

  const requestHandler = axiosLib.create({
    headers: {
      Authorization: `Bearer ${token}`,
      ...headers
    },
    baseURL: BASE_URL,
    ...config
  })

  requestHandler.interceptors.request.use(
    async function (config) {
      if (useLoader) store.dispatch(uiSlice.actions.setIsFetching(true))
      config.headers[LANGUAGE_HEADER] = getCurrentLanguage()
      return config
    },
    function (error: AxiosError) {
      store.dispatch(uiSlice.actions.setIsFetching(false))
      const status = error.response?.status || error.status || 0
      if ([403, 401].includes(status)) {
        store.dispatch(userSlice.actions.logOut())
        router.navigate(routerObj.signIn.path)
        return null
      } else if (status === 500) {
        store.dispatch(uiSlice.actions.setError({ errorMessage: i18n.t('shared.requestHandler.error'), errorStatus: error.status }))
        return Promise.reject(error)
      }
      if (props.useErrorToast) {
        const errorMessage = (() => {
          const data = error.response?.data as any
          if (data?.detail) return i18n.t('shared.requestHandler.internalError')
          if (typeof data?.message === 'string') {
            return data.message
          }
          return i18n.t('shared.requestHandler.unexpectedError')
        })()
        store.dispatch(uiSlice.actions.setError({ errorMessage, errorStatus: error.status }))
      }
      return Promise.reject(error)
    }
  )

  requestHandler.interceptors.response.use(
    async function (config) {
      store.dispatch(uiSlice.actions.setIsFetching(false))
      return config
    },
    function (error: AxiosError) {
      store.dispatch(uiSlice.actions.setIsFetching(false))
      if (handleResponseError) {
        handleResponseError(error)
        return Promise.reject(error)
      }

      const status = error.response?.status || error.status || 0
      if ([403, 401].includes(status)) {
        store.dispatch(userSlice.actions.logOut())
        router.navigate(routerObj.signIn.path)
        return null
      } else if (status === 500) {
        store.dispatch(uiSlice.actions.setError({ errorMessage: i18n.t('shared.requestHandler.error'), errorStatus: error.status }))
        return Promise.reject(error)
      }
      // Handle the error
      if (props.useErrorToast) {
        const errorMessage = (() => {
          const data = error.response?.data as any
          if (data?.detail) return i18n.t('shared.requestHandler.internalError')
          if (typeof data?.message === 'string') {
            return data.message
          }
          return i18n.t('shared.requestHandler.unexpectedError')
        })()
        store.dispatch(uiSlice.actions.setError({ errorMessage, errorStatus: error.status }))
      }
      return Promise.reject(error)
    }
  )

  return requestHandler
}
